"use client"
import { FaCar, FaUsers, FaGlobe, FaAward, FaHandshake, FaShieldAlt } from "react-icons/fa";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import "./About.css";

// Company values data
const companyValues = [
  {
    icon: <FaHandshake />,
    title: "Trust & Reliability",
    description:
      "We build lasting partnerships with our customers through transparent, reliable service and honest practices.",
  },
  {
    icon: <FaShieldAlt />,
    title: "Quality Assurance",
    description:
      "Our network of trusted suppliers and auction partners ensures every vehicle meets rigorous quality standards.",
  },
  {
    icon: <FaGlobe />,
    title: "Global Reach",
    description:
      "We connect customers worldwide with high-quality vehicles, making international buying smooth and secure.",
  },
];

// Stats Component with animation
const StatsSection = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  const companyStats = [
    {
      icon: <FaCar />,
      number: 1000,
      suffix: "+",
      label: "Vehicles Delivered",
    },
    {
      icon: <FaUsers />,
      number: 800,
      suffix: "+",
      label: "Happy Customers",
    },
    {
      icon: <FaGlobe />,
      number: 30,
      suffix: "+",
      label: "Countries Served",
    },
    {
      icon: <FaAward />,
      number: 16,
      suffix: "+",
      label: "Years of Experience",
    },
  ];

  return (
    <div className="stats-section" ref={ref}>
      <div className="stats-container">
        {companyStats.map((stat, index) => (
          <div key={index} className="stat-card">
            <div className="stat-icon">{stat.icon}</div>
            <div className="stat-number">
              {inView ? (
                <CountUp end={stat.number} duration={2.5} separator="," suffix={stat.suffix} />
              ) : (
                "0"
              )}
            </div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Values Component
const ValuesSection = () => {
  return (
    <div className="values-section">
      <h2 className="section-title">Our Values</h2>
      <div className="values-grid">
        {companyValues.map((value, index) => (
          <div key={index} className="value-card">
            <div className="value-icon">{value.icon}</div>
            <h3 className="value-title">{value.title}</h3>
            <p className="value-description">{value.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Component
export default function AboutUs() {
  return (
    <div className="about-page">
      {/* Hero Section */}
      <div className="about-hero">
        <div className="about-hero-content">
          <h1 className="about-hero-title">About Shipment Solutions LLC</h1>
          <p className="about-hero-subtitle">
            Global vehicle procurement and shipping made simple, with transparency and reliability you can count on.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="about-main-content">
        {/* Company Story */}
        <div className="story-section">
          <div className="story-content">
            <div className="story-text">
              <h2 className="section-title">Our Story</h2>
              <p className="story-paragraph">
                Shipment Solutions LLC was founded to transform the international vehicle trade by making it more trustworthy, transparent, and customer-friendly. We partner with reputable auction houses and suppliers worldwide to offer a diverse inventory of quality vehicles at competitive prices.
              </p>
              <p className="story-paragraph">
                From sourcing to shipping, our team manages every step of the process so our customers can purchase with confidence and peace of mind. We handle the paperwork, coordinate logistics, and provide reliable support at every stage.
              </p>
              <p className="story-paragraph">
                Our mission is to be a dependable link between global vehicle markets and buyers worldwide — providing value, trust, and an excellent customer experience every time.
              </p>
            </div>
                <div className="story-logo" style={{ flex: "0 1 280px", textAlign: "center", minWidth: 220 }}>
            <img
              src="/assets/completelogo.png"
              alt="Shipment Solutions Logo"
              style={{ maxWidth: "220px", width: "80%", height: "auto", margin: "0 auto", display: "block", filter: "drop-shadow(0 4px 24px #8882)" }}
            />
          </div>
          </div>
        </div>

        {/* Stats Section */}
        <StatsSection />

        {/* Mission & Vision */}
        <div className="mission-section">
          <div className="mission-grid">
            <div className="mission-card">
              <h3 className="mission-title">Our Mission</h3>
              <p className="mission-text">
                To empower global customers with a seamless, transparent, and reliable vehicle purchasing and shipping experience.
              </p>
            </div>
            <div className="mission-card">
              <h3 className="mission-title">Our Vision</h3>
              <p className="mission-text">
                To become the most trusted partner for international vehicle sourcing and logistics, setting new standards for quality, trust, and customer service in the global automotive market.
              </p>
            </div>
          </div>
        </div>

        {/* Values Section */}
        <ValuesSection />

        {/* Why Choose Us */}
        <div className="why-choose-section">
          <h2 className="section-title">Why Choose Shipment Solutions LLC?</h2>
          <div className="features-grid">
            <div className="feature-item">
              <h4 className="feature-title">Quality Assurance</h4>
              <p className="feature-text">
                Each vehicle is carefully inspected and sourced from trusted suppliers to ensure premium standards.
              </p>
            </div>
            <div className="feature-item">
              <h4 className="feature-title">Worldwide Logistics</h4>
              <p className="feature-text">
                We handle global shipping and documentation to make your purchase as smooth as possible.
              </p>
            </div>
            <div className="feature-item">
              <h4 className="feature-title">Competitive Prices</h4>
              <p className="feature-text">
                Our relationships with reputable partners allow us to offer high-quality vehicles at fair prices.
              </p>
            </div>
            <div className="feature-item">
              <h4 className="feature-title">Expert Guidance</h4>
              <p className="feature-text">
                Our experienced team supports you from selection through shipping, answering all your questions.
              </p>
            </div>
            <div className="feature-item">
              <h4 className="feature-title">Transparent Process</h4>
              <p className="feature-text">
                Clear pricing, honest condition reports, and no hidden surprises — just straight talk.
              </p>
            </div>
            <div className="feature-item">
              <h4 className="feature-title">After-Sales Support</h4>
              <p className="feature-text">
                We’re here to help even after delivery to make sure you are fully satisfied with your purchase.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
